/**
 * Weather (สภาพอากาศ, data/weather.ts): the zone's weather turns from one kind to the next over the
 * real minutes, drawn from the zone's weights through the World's `Rng`, and only as `World.step`
 * runs. The state is `world.weather`, plain data that the server sends with every snapshot (as the
 * hour), so every player in the zone sees the same sky.
 *
 * A world starts clear; the first turn comes after `WEATHER_TIMING.firstSpell`, so nothing is drawn
 * from the random stream before then. No gameplay effect yet: the renderer and the HUD read it.
 *
 * The `weather` command (GM worlds only) sets a kind at once (testing, shots), or turns to the next.
 */
import { isWeatherKind, nextWeather, WEATHER, WEATHER_TIMING } from '../data/weather';
import type { CommandHandlers, SystemHost, WorldSystem } from './types';

export class WeatherSystem implements WorldSystem {
  readonly name = 'weather';

  constructor(private readonly h: SystemHost) {}

  readonly commands: CommandHandlers = {
    weather: (c) => {
      const w = this.h.world;
      if (!w.gmEnabled) return;
      const s = w.weather;
      if (c.kind === null) {
        // The next kind, turning as it would on its own.
        s.left = 0;
        this.turn();
      } else if (isWeatherKind(c.kind)) {
        Object.assign(s, { kind: c.kind, from: c.kind, blend: 1, left: this.spell() });
      } else return;
      this.h.notice(`สภาพอากาศ: ${WEATHER[s.kind].name}`);
    },
  };

  step(dt: number): void {
    const s = this.h.world.weather;
    if (s.blend < 1) s.blend = Math.min(1, s.blend + dt / WEATHER_TIMING.transition);
    if ((s.left -= dt) <= 0) this.turn();
  }

  /** Into the next kind: the turn starts now, and a new spell is drawn. */
  private turn(): void {
    const w = this.h.world;
    const s = w.weather;
    const next = nextWeather(w.map.zone, s.kind, this.h.random());
    Object.assign(s, { from: s.kind, kind: next, blend: next === s.kind ? 1 : 0, left: this.spell() });
  }

  private spell(): number {
    const { minSpell, maxSpell } = WEATHER_TIMING;
    return minSpell + (maxSpell - minSpell) * this.h.random();
  }
}
